import { Injectable } from '@angular/core';
import { Observable, tap, throwError } from 'rxjs';
import { NavController, ToastController } from '@ionic/angular/standalone';

import { AuthControllerService } from '../../api-clients/generated/services/AnmcoBackend/api/authController.service';
import { UserControllerService } from '../../api-clients/generated/services/AnmcoBackend/api/userController.service';
import { LoginForm } from '../../api-clients/generated/services/AnmcoBackend/model/loginForm';
import { LoginResponse } from '../../api-clients/generated/services/AnmcoBackend/model/loginResponse';
import { RefreshTokenForm } from '../../api-clients/generated/services/AnmcoBackend/model/refreshTokenForm';
import { UserRegistrationForm } from '../../api-clients/generated/services/AnmcoBackend/model/userRegistrationForm';
import { UserResponseDTO } from '../../api-clients/generated/services/AnmcoBackend/model/userResponseDTO';
import { GrantType } from '../../api-clients/generated/services/AnmcoBackend/model/grantType';
import { LocalStorageService } from './local-storage.service';
import { JsBridgeService } from './js-bridge.service';

@Injectable({ providedIn: 'root' })
export class AuthService {

  constructor(
    private authController: AuthControllerService,
    private userController: UserControllerService,
    private localStorageService: LocalStorageService,
    private jsBridge: JsBridgeService,
    private navController: NavController,
    private toastController: ToastController,
  ) {}

  login(username: string, password: string): Observable<LoginResponse> {
    const form: LoginForm = {
      username,
      password,
      grant_type: GrantType.Password,
    };
    return this.authController.login(form).pipe(
      tap(response => this.saveTokens(response))
    );
  }

  register(form: UserRegistrationForm): Observable<UserResponseDTO> {
    return this.userController.registerUser(form);
  }

  logout(): void {
    this.localStorageService.cleanLoginProperties();
  }

  isAuthenticated(): boolean {
    return !!this.localStorageService.getAccessToken();
  }

  getAccessToken(): string | null {
    return this.localStorageService.getAccessToken();
  }

  refreshToken(): Observable<LoginResponse> {
    const refreshTokenValue = this.localStorageService.getRefreshToken();
    const username = this.localStorageService.getEmail();

    if (!refreshTokenValue || !username) {
      return throwError(() => new Error('Missing refresh token or username'));
    }

    const form: RefreshTokenForm = {
      refresh_token: refreshTokenValue,
      username,
      grant_type: GrantType.RefreshToken,
    };

    return this.authController.refreshToken(form).pipe(
      tap(response => {
        this.saveTokens(response);
        if (response.access_token) {
          const userId = this.localStorageService.getUserId() || '';
          this.jsBridge.updateAccessToken({ userId, accessToken: response.access_token });
        }
      })
    );
  }

  async handleRefreshFailure(): Promise<void> {
    this.logout();
    const toast = await this.toastController.create({
      message: 'Sessione scaduta. Effettua nuovamente il login.',
      color: 'danger',
      duration: 4000,
      position: 'bottom',
    });
    await toast.present();
    this.navController.navigateRoot('/auth/login');
  }

  private saveTokens(response: LoginResponse): void {
    if (response.access_token) {
      this.localStorageService.saveLogInProperties(
        response.access_token,
        response.refresh_token || '',
        response.expires_in?.toString() || '',
      );
    }
  }
}
